import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuth from "./useAuth";
import { getCoaRequest, submitCoa, updateCoa, updateCoaApprovals } from "./ftoService";

const EMPTY_COA = {
    empId: "",
    startDate: new Date().toISOString().split("T")[0],  // today "yyyy-MM-dd"
    endDate: "",
    modifiedStartTime: "",
    modifiedEndTime: "",
    employeeComment: "",
    supervisorNote: "",
    hrNote: "",
    supervisorApproval: null,
    hrApproval: null,
};

const useCoaForm = ({ coaId = 0 } = {}) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const isNew = coaId === 0;

    const [form, setForm] = useState({ ...EMPTY_COA, empId: auth.empId });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [isSupervisorOf, setIsSupervisorOf] = useState(false);

    useEffect(() => {
        if (isNew) return;
        (async () => {
            try {
                setLoading(true);
                const { data } = await getCoaRequest(coaId);
                setIsSupervisorOf(data.isSupervisorOf ?? false);
                setForm({
                    empId: String(data.empId),
                    startDate: data.startDate?.split("T")[0] ?? "",
                    endDate: data.endDate?.split("T")[0] ?? "",
                    modifiedStartTime: data.modifiedStartTime ?? "",
                    modifiedEndTime: data.modifiedEndTime ?? "",
                    employeeComment: data.employeeComment ?? "",
                    supervisorNote: data.supervisorNote ?? "",
                    hrNote: data.hrNote ?? "",
                    supervisorApproval: data.supervisorApproval ?? null,
                    hrApproval: data.hrApproval ?? null,
                });
            } catch {
                toast.error("Failed to load COA request.");
            } finally {
                setLoading(false);
            }
        })();
    }, [coaId]);

    const isOwnRequest = form.empId === auth.empId;
    const canEditEmployee = isNew ? true : isOwnRequest;
    const canEditSupervisor = !isNew && (auth.isSuperUser || isSupervisorOf);
    const canEditHR = !isNew && (auth.isSuperUser || auth.isHR);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        if (!form.startDate) return "Date is required.";
        if (!form.modifiedStartTime) return "Modified start time is required.";
        if (!form.modifiedEndTime) return "Modified end time is required.";
        if (!form.employeeComment.trim()) return "Employee comment is required.";
        return null;
    };

    const handleSubmit = async () => {
        const err = validate();
        if (err) { toast.warning(err); return; }
        try {
            setSaving(true);

            const payload = {
                empId: form.empId,
                startDate: form.startDate,
                endDate: form.endDate || null,
                modifiedStartTime: form.modifiedStartTime || null,
                modifiedEndTime: form.modifiedEndTime || null,
                employeeComment: form.employeeComment || "",
            };

            console.log("🚀 COA Submit Payload:", JSON.stringify(payload, null, 2));

            if (isNew) {
                const res = await submitCoa(payload);
                console.log("✅ COA Submit Response:", res.data);
                toast.success("COA request submitted.");
                navigate("/attendance");
            } else {
                if (isOwnRequest) await updateCoa(coaId, payload);
                await updateCoaApprovals(coaId, {
                    supervisorNote: form.supervisorNote ?? "",
                    hrNote: form.hrNote ?? "",
                    supervisorApproval: form.supervisorApproval ?? null,
                    hrApproval: form.hrApproval ?? null,
                    hrOverrideSupervisor: form.hrOverrideSupervisor ?? false,   // HR override (2026-07)
                });
                toast.success("COA request updated.");
                navigate("/attendance");
            }
        } catch (error) {
            console.error("❌ Status:", error?.response?.status);
            console.error("❌ Response:", JSON.stringify(error?.response?.data, null, 2));
            // Round 2c: surface the server's actual reason (same fix as FTO).
            toast.error(
                error?.response?.data?.message
                || (typeof error?.response?.data === "string" ? error.response.data : null)
                || "Failed to save COA request.");
        } finally {
            setSaving(false);
        }
    };

    return {
        form, setField, loading, saving, isNew, auth,
        canEditEmployee, canEditSupervisor, canEditHR,
        handleSubmit,
    };
};

export default useCoaForm;